import {
  Button,
  Box,
  Stack,
  HStack,
  useToast,
  Badge,
  Link,
  Heading,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import * as services from '@/services/license';
import type { License } from '@prisma/client';
import format from 'date-fns/format';
import copy from 'copy-to-clipboard';
import { useIntl } from 'react-intl';

interface Props {
  isLoggedIn: boolean;
  publicAddress: string;
  onUnbindDevice: (licenseCode: string) => void;
}

export function MyLicense(props: Props) {
  const [licenseList, setLicenseList] = useState<License[]>([]);
  const toast = useToast({
    position: 'top',
    duration: 3000,
  });
  const intl = useIntl();

  useEffect(() => {
    if (!props.isLoggedIn) {
      return;
    }
    services
      .getLicenseList({
        publicAddress: props.publicAddress,
      })
      .then((res) => {
        if (res.success) {
          setLicenseList(res.payload || []);
        }
      });
  }, [props.publicAddress, props.isLoggedIn]);

  if (!props.isLoggedIn) {
    return null;
  }

  const hasLeastOneValidLicense = licenseList.some((license) => {
    return new Date(license.validOf).getTime() > Date.now();
  });

  return (
    <Stack>
      {hasLeastOneValidLicense && (
        <HStack>
          <Link
            href="https://discord-superman.notion.site/9452fcde476e410da4c14ee0f38a0523"
            target="_blank"
          >
            <Button>
              {intl.formatMessage({
                defaultMessage: '下载客户端',
                id: 'license.download_client',
              })}
            </Button>
          </Link>

          <Link
            href="https://discord-superman.notion.site/Discord-SuperMan-27f76bfcce784da49c69a8c012a7077a"
            target="_blank"
          >
            <Button>
              {intl.formatMessage({
                defaultMessage: '查看使用手册',
                id: 'license.view_manual',
              })}
            </Button>
          </Link>
          <Link href="/paid-member/join-member-channel">
            <Button>
              {intl.formatMessage({
                defaultMessage: '加入会员频道',
                id: 'license.join_member_channel',
              })}
            </Button>
          </Link>
        </HStack>
      )}
      <Stack pt={5}>
        <Heading as="h3" size="sm">
          {intl.formatMessage({
            defaultMessage: '我的注册码',
            id: 'license.my_registration_code',
          })}
        </Heading>
        {!licenseList.length && (
          <Text fontSize="sm" color="gray">
            {intl.formatMessage({
              defaultMessage: '没有购买记录~',
              id: 'license.no_purchase_record',
            })}
          </Text>
        )}
        {licenseList.map((license) => {
          const isValid =
            new Date(license.validOf).getTime() > new Date().getTime();
          return (
            <Box key={license.id}>
              <HStack>
                {intl.formatMessage({
                  defaultMessage: '注册码：',
                  id: 'license.registration_code_label',
                })}
                <span
                  style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                  onClick={() => {
                    const copied = copy(license.code);
                    if (copied) {
                      toast({
                        title: intl.formatMessage({
                          defaultMessage: '注册码已复制到剪贴板',
                          id: 'license.code_copied',
                        }),
                      });
                    }
                  }}
                >
                  {license.code}
                </span>
                <Button
                  onClick={() => {
                    props.onUnbindDevice?.(license.code);
                  }}
                  variant="link"
                  size="sm"
                >
                  {intl.formatMessage({
                    defaultMessage: '解绑设备',
                    id: 'license.unbind_device',
                  })}
                </Button>
              </HStack>
              <Box alignItems="center" display="flex">
                {intl.formatMessage({
                  defaultMessage: '状态：',
                  id: 'license.status_label',
                })}
                <Badge colorScheme={isValid ? 'green' : 'red'} mr={1}>
                  {isValid
                    ? intl.formatMessage({
                        defaultMessage: '有效',
                        id: 'license.status_valid',
                      })
                    : intl.formatMessage({
                        defaultMessage: '过期',
                        id: 'license.status_expired',
                      })}
                </Badge>
                {format(new Date(license.validOf), 'yyyy-MM-dd HH:mm:ss')}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
